import React, {useState} from "react";
import ArrowDropDown from "@mui/icons-material/ArrowDropDown";
import ArrowBack from "@mui/icons-material/ArrowBack";
import {Button, Grid, IconButton, MenuItem, MenuList, Popover} from "@mui/material";
import {css} from "@emotion/css";
import moment from "moment";
import {DateField} from "./DateField";
import {
    Column,
    FilterInput,
    FilterWrapper,
    lookupNestedProp,
    matchAnywhere,
    NestedKeyOf,
    RenderFilterProps,
    sortDate,
    TID,
} from "./scroller/Table";

// picks the quick options and how an ambiguous period name resolves:
// "historical" looks backwards from today, "future" looks forwards, "both"
// looks in either direction
export type DateFilterMode = "historical" | "future" | "both";

// view-only date column with the advanced date filter (presets + custom range)
export function DateFilterCol<T extends TID>(name: string, key: NestedKeyOf<T>, format: string, width = 100, mode: DateFilterMode = "historical"): Column<T> {
    return {
        name: name,
        render: (o: T) => {
            const v = lookupNestedProp(o, key);
            if(!v) return "";
            return moment.utc(v).format(format);
        },
        width: width,
        filter: dateFilterMatch(key, mode),
        renderFilter: (p: RenderFilterProps) => <DateFilterForCol value={p.value} onChange={p.onChange} mode={mode} />,
        sort: sortDate(key),
    } as any;
}

// swaps an existing column's filter/sort for the advanced date filter, keeping
// its render and editing behaviour. Use this when DateFilterCol can't replace
// the column outright (editable cells, custom renders).
export function WithDateFilter<T extends TID>(col: Column<T>, key: NestedKeyOf<T>, mode: DateFilterMode = "historical"): Column<T> {
    return Object.assign(col, {
        filter: dateFilterMatch<T>(key, mode),
        renderFilter: (p: RenderFilterProps) => <DateFilterForCol value={p.value} onChange={p.onChange} mode={mode} />,
        sort: sortDate<T>(key),
    });
}

// quick options are concrete values, so the filter cell shows what was picked
// and the same value can be typed by hand
function presetOptions(mode: DateFilterMode): {display: string; value: string}[] {
    if(mode === "future") {
        return [
            weekOption(moment()),
            weekOption(moment().add(1, "week")),
            monthOption(moment()),
            monthOption(moment().add(1, "month")),
            quarterOption(moment()),
            quarterOption(moment().add(1, "quarter")),
        ];
    }

    // weeks only look forward and quarters only cover the current one, to keep
    // the menu short. The neighbouring months cover the near past either way.
    if(mode === "both") {
        return [
            weekOption(moment()),
            weekOption(moment().add(1, "week")),
            monthOption(moment().add(-1, "month")),
            monthOption(moment()),
            monthOption(moment().add(1, "month")),
            quarterOption(moment()),
            yearOption(moment()),
        ];
    }

    return [
        monthOption(moment()),
        monthOption(moment().add(-1, "month")),
        quarterOption(moment()),
        quarterOption(moment().add(-1, "quarter")),
        yearOption(moment()),
    ];
}

// a week number is hard to place on its own, so the menu shows its dates too
function weekOption(base: moment.Moment) {
    const value = "W" + base.isoWeek();
    return {
        value: value,
        display: value + " (" + base.clone().startOf("isoWeek").format("MMM D") + " - " + base.clone().endOf("isoWeek").format("MMM D") + ")",
    };
}

function monthOption(base: moment.Moment) {
    const value = base.format("MMMM");
    return {value: value, display: value};
}

function quarterOption(base: moment.Moment) {
    const value = "Q" + base.quarter();
    return {value: value, display: value};
}

function yearOption(base: moment.Moment) {
    const value = base.format("YYYY");
    return {value: value, display: value};
}

export function DateFilterForCol(props: {
    value: string;
    onChange(value: string): void;
    mode?: DateFilterMode;
}) {
    const [anchor, setAnchor] = useState<HTMLElement|null>(null);
    const [view, setView] = useState<"list"|"custom">("list");
    const [from, setFrom] = useState<string|null>(null);
    const [to, setTo] = useState<string|null>(null);

    const custom = parseCustomValue(props.value);

    return (
        <FilterWrapper>
            <FilterInput
                type="text" placeholder="Filter"
                value={custom ? "(custom)" : props.value}
                onChange={e => props.onChange(e.target.value)}
            />
            <button className={arrowStyle} onClick={e => {
                setView("list");
                setAnchor(e.currentTarget);
            }}>
                <ArrowDropDown fontSize="inherit" />
            </button>
            {anchor && <Popover
                open
                anchorEl={anchor}
                onClose={() => setAnchor(null)}
                anchorOrigin={{vertical: "bottom", horizontal: "right"}}
                transformOrigin={{vertical: "top", horizontal: "right"}}
            >
                {view === "list" ? <MenuList dense>
                    <MenuItem onClick={() => {
                        props.onChange("");
                        setAnchor(null);
                    }}><i>None</i></MenuItem>
                    {presetOptions(props.mode || "historical").map(p => <MenuItem key={p.value} onClick={() => {
                        props.onChange(p.value);
                        setAnchor(null);
                    }}>{p.display}</MenuItem>)}
                    <MenuItem onClick={() => {
                        setFrom(custom && custom.from ? moment(custom.from, "YYYY-MM-DD").toJSON() : null);
                        setTo(custom && custom.to ? moment(custom.to, "YYYY-MM-DD").toJSON() : null);
                        setView("custom");
                    }}>Custom Range</MenuItem>
                </MenuList> : <div style={{padding: 8, width: 240}}>
                    <div style={{display: "flex", alignItems: "center", paddingBottom: 8}}>
                        <IconButton size="small" onClick={() => setView("list")}>
                            <ArrowBack fontSize="inherit" />
                        </IconButton>
                        <div style={{paddingLeft: 4, fontWeight: 500}}>Custom Range</div>
                    </div>
                    <Grid container direction="column" spacing={1}>
                        <Grid item>
                            <DateField label="From" nullable fullWidth initialValue={from} onChange={setFrom} />
                        </Grid>
                        <Grid item>
                            <DateField label="To" nullable fullWidth initialValue={to} onChange={setTo} />
                        </Grid>
                        <Grid item>
                            <Button
                                fullWidth variant="contained" size="small"
                                disabled={!from && !to}
                                onClick={() => {
                                    const fmt = (v: string|null) => v ? moment(v).format("YYYY-MM-DD") : "";
                                    props.onChange("custom:" + fmt(from) + ":" + fmt(to));
                                    setAnchor(null);
                                }}
                            >Apply</Button>
                        </Grid>
                    </Grid>
                </div>}
            </Popover>}
        </FilterWrapper>
    );
}

// matches rows by date range when the filter value is a preset ("September",
// "Q3", "W37", "2026") or an applied custom range; otherwise falls back
// to text matching on the raw value and its "MMMM D" rendering
export function dateFilterMatch<T extends object>(key: NestedKeyOf<T>, mode: DateFilterMode = "historical") {
    return (el: T, input: string) => {
        const raw = lookupNestedProp(el, key);
        const range = filterRange(input, mode);
        if(range) {
            if(!raw) return false;

            const day = moment.utc(raw).format("YYYY-MM-DD");
            if(range.from && day < range.from) return false;
            if(range.to && day > range.to) return false;
            return true;
        }

        if(!raw) return false;
        return matchAnywhere(raw.toString() + " " + moment.utc(raw).format("MMMM D"), input);
    };
}

// input arrives lowercased+trimmed from the filter cell
function filterRange(input: string, mode: DateFilterMode): {from: string; to: string} | null {
    const span = (base: moment.Moment, unit: "isoWeek" | "month" | "quarter" | "year") => ({
        from: base.clone().startOf(unit).format("YYYY-MM-DD"),
        to: base.clone().endOf(unit).format("YYYY-MM-DD"),
    });

    // a week/month/quarter name gives no year, so the mode decides which one it
    // means: "historical" reads a later period as last year's, "future" reads an
    // earlier period as next year's (e.g. "January" picked as next-month in
    // December), and "both" takes whichever occurrence is nearer to today. On a
    // half-cycle tie "both" stays in the current year.
    const yearOffset = (index: number, current: number, cycle: number) => {
        if(mode === "historical") return index > current ? -1 : 0;
        if(mode === "future") return index < current ? 1 : 0;

        const diff = index - current;
        if(diff > cycle / 2) return -1;
        if(diff < -cycle / 2) return 1;
        return 0;
    };

    // weeks resolve through isoWeekYear, so week 1 and week 52/53 land in the
    // year that actually holds them
    const w = /^w([0-9]{1,2})$/.exec(input);
    if(w) {
        const num = parseInt(w[1]);
        if(num >= 1 && num <= 53) {
            const year = moment().isoWeekYear() + yearOffset(num, moment().isoWeek(), moment().isoWeeksInYear());
            return span(moment().isoWeekYear(year).isoWeek(num), "isoWeek");
        }
    }

    const monthIndex = moment.months().map(m => m.toLowerCase()).indexOf(input);
    if(monthIndex !== -1) {
        const base = moment().month(monthIndex);
        return span(base.add(yearOffset(monthIndex, moment().month(), 12), "year"), "month");
    }

    const q = /^q([1-4])$/.exec(input);
    if(q) {
        const num = parseInt(q[1]);
        const base = moment().quarter(num);
        return span(base.add(yearOffset(num, moment().quarter(), 4), "year"), "quarter");
    }

    if(/^\d{4}$/.test(input)) {
        return span(moment(input, "YYYY"), "year");
    }

    return parseCustomValue(input);
}

function parseCustomValue(input: string): {from: string; to: string} | null {
    const m = /^custom:(\d{4}-\d{2}-\d{2})?:(\d{4}-\d{2}-\d{2})?$/.exec(input.trim());
    if(!m) return null;
    return {from: m[1] || "", to: m[2] || ""};
}

const arrowStyle = css({
    display: "flex",
    alignItems: "center",
    background: "none",
    border: "none",
    padding: 0,
    paddingRight: 2,
    cursor: "pointer",
    "&:hover": {
        opacity: 0.7,
    },
});
